// ===========================================
// Overlay Renderer for Promotional Overlays
// ===========================================

export interface OverlayConfig {
  id: string;
  type: 'ticker' | 'badge' | 'lowerThird';
  enabled: boolean;
  config: {
    text: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    backgroundColor?: string;
    textColor?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    borderRadius?: number;
    padding?: number;
    scrollSpeed?: number;
    scrollDirection?: 'left' | 'right';
    position?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    animation?: 'none' | 'fadeIn' | 'slideIn' | 'pulse';
  };
}

class OverlayRenderer {
  private container: HTMLElement | null = null;
  private overlayContainer: HTMLElement | null = null;
  private overlays: Map<string, HTMLElement> = new Map();
  private animationFrames: Map<string, number> = new Map();
  private styleElements: HTMLStyleElement[] = []; // Track style elements for cleanup

  initialize(containerId: string): void {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('[OverlayRenderer] Container not found:', containerId);
      return;
    }

    // Create overlay container
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.id = 'overlay-container';
    this.overlayContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 9999;
      overflow: hidden;
    `;
    this.container.appendChild(this.overlayContainer);

    console.log('[OverlayRenderer] Initialized');
  }

  setOverlays(overlays: OverlayConfig[]): void {
    if (!this.overlayContainer) {
      console.error('[OverlayRenderer] Container not initialized');
      return;
    }

    // Clear existing overlays
    this.clear();

    // Filter enabled overlays and render
    const enabledOverlays = overlays.filter(o => o.enabled);
    console.log('[OverlayRenderer] Rendering', enabledOverlays.length, 'overlays');

    for (const overlay of enabledOverlays) {
      this.renderOverlay(overlay);
    }
  }

  private renderOverlay(overlay: OverlayConfig): void {
    if (!this.overlayContainer) return;

    let element: HTMLElement;

    switch (overlay.type) {
      case 'badge':
        element = this.createBadgeOverlay(overlay);
        break;
      case 'ticker':
        element = this.createTickerOverlay(overlay);
        break;
      case 'lowerThird':
        element = this.createLowerThirdOverlay(overlay);
        break;
      default:
        console.warn('[OverlayRenderer] Unknown overlay type:', overlay.type);
        return;
    }

    // Apply animation
    this.applyAnimation(element, overlay.config.animation || 'fadeIn');

    this.overlayContainer.appendChild(element);
    this.overlays.set(overlay.id, element);
  }

  private createBadgeOverlay(overlay: OverlayConfig): HTMLElement {
    const { config } = overlay;
    const element = document.createElement('div');
    element.className = 'overlay-badge';

    // Position based on config
    const positions: Record<string, { top?: string; bottom?: string; left?: string; right?: string }> = {
      topLeft: { top: '20px', left: '20px' },
      topRight: { top: '20px', right: '20px' },
      bottomLeft: { bottom: '20px', left: '20px' },
      bottomRight: { bottom: '20px', right: '20px' },
    };

    const pos = positions[config.position || 'topRight'] || positions.topRight;

    element.style.cssText = `
      position: absolute;
      ${pos.top ? `top: ${pos.top};` : ''}
      ${pos.bottom ? `bottom: ${pos.bottom};` : ''}
      ${pos.left ? `left: ${pos.left};` : ''}
      ${pos.right ? `right: ${pos.right};` : ''}
      background-color: ${config.backgroundColor || '#ef4444'};
      color: ${config.textColor || '#ffffff'};
      font-size: ${config.fontSize || 24}px;
      font-weight: ${config.fontWeight || 'bold'};
      border-radius: ${config.borderRadius || 8}px;
      padding: ${config.padding || 16}px ${(config.padding || 16) * 1.5}px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      text-transform: uppercase;
      letter-spacing: 1px;
      white-space: nowrap;
    `;

    element.textContent = config.text;

    return element;
  }

  private createTickerOverlay(overlay: OverlayConfig): HTMLElement {
    const { config } = overlay;
    const container = document.createElement('div');
    container.className = 'overlay-ticker';

    // Get position from config, default to bottom
    const yPos = config.y !== undefined ? `${config.y}%` : '90%';
    const height = config.height !== undefined ? `${config.height}%` : '10%';

    container.style.cssText = `
      position: absolute;
      left: 0;
      right: 0;
      top: ${yPos};
      height: ${height};
      background-color: ${config.backgroundColor || '#000000'};
      color: ${config.textColor || '#ffffff'};
      font-size: ${config.fontSize || 24}px;
      font-weight: ${config.fontWeight || 'bold'};
      overflow: hidden;
      display: flex;
      align-items: center;
    `;

    const text = document.createElement('span');
    text.className = 'ticker-text';
    text.style.cssText = `
      white-space: nowrap;
      position: absolute;
      animation: ticker-scroll ${Math.max(5, config.text.length * 0.2)}s linear infinite;
    `;
    text.textContent = config.text;

    // Add CSS animation
    const style = document.createElement('style');
    const animationName = `ticker-scroll-${overlay.id}`;
    const direction = config.scrollDirection === 'right' ? -1 : 1;

    style.textContent = `
      @keyframes ${animationName} {
        from {
          transform: translateX(${direction === 1 ? '100%' : '-100%'});
        }
        to {
          transform: translateX(${direction === 1 ? '-100%' : '100%'});
        }
      }
    `;
    document.head.appendChild(style);
    this.styleElements.push(style); // Track for cleanup

    text.style.animation = `${animationName} ${Math.max(10, 200 / (config.scrollSpeed || 50))}s linear infinite`;

    container.appendChild(text);
    return container;
  }

  private createLowerThirdOverlay(overlay: OverlayConfig): HTMLElement {
    const { config } = overlay;
    const container = document.createElement('div');
    container.className = 'overlay-lower-third';

    const yPos = config.y !== undefined ? `${config.y}%` : '75%';
    const height = config.height !== undefined ? `${config.height}%` : '25%';

    container.style.cssText = `
      position: absolute;
      left: 0;
      right: 0;
      top: ${yPos};
      height: ${height};
      background: linear-gradient(to right, ${config.backgroundColor || '#1a1a1a'}, ${config.backgroundColor || '#1a1a1a'}dd, transparent);
      color: ${config.textColor || '#ffffff'};
      display: flex;
      align-items: center;
      padding: 0 40px;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    const textLine = document.createElement('div');
    textLine.style.cssText = `
      font-size: ${config.fontSize || 32}px;
      font-weight: ${config.fontWeight || 'bold'};
      text-transform: uppercase;
      letter-spacing: 2px;
    `;
    textLine.textContent = config.text;

    // Add accent bar
    const accent = document.createElement('div');
    accent.style.cssText = `
      width: 60px;
      height: 4px;
      background-color: ${config.backgroundColor === '#1a1a1a' ? '#ef4444' : config.textColor || '#ffffff'};
      border-radius: 2px;
    `;

    content.appendChild(accent);
    content.appendChild(textLine);
    container.appendChild(content);

    return container;
  }

  private applyAnimation(element: HTMLElement, animation: string): void {
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';

    // Trigger animation after a brief delay
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        switch (animation) {
          case 'fadeIn':
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
            break;
          case 'slideIn':
            element.style.opacity = '1';
            element.style.transform = 'translateX(0)';
            break;
          case 'pulse':
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
            element.style.animation = 'pulse 2s ease-in-out infinite';
            // Add pulse keyframes if not exists
            if (!document.getElementById('pulse-keyframes')) {
              const style = document.createElement('style');
              style.id = 'pulse-keyframes';
              style.textContent = `
                @keyframes pulse {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.05); }
                }
              `;
              document.head.appendChild(style);
            }
            break;
          case 'none':
          default:
            element.style.opacity = '1';
            element.style.transform = 'none';
            element.style.transition = 'none';
            break;
        }
      });
    });
  }

  clear(): void {
    // Cancel any running animations
    for (const frameId of this.animationFrames.values()) {
      cancelAnimationFrame(frameId);
    }
    this.animationFrames.clear();

    // Remove all overlay elements
    for (const element of this.overlays.values()) {
      element.remove();
    }
    this.overlays.clear();

    // Remove dynamically added style elements
    for (const style of this.styleElements) {
      style.remove();
    }
    this.styleElements = [];
  }

  destroy(): void {
    this.clear();
    if (this.overlayContainer) {
      this.overlayContainer.remove();
      this.overlayContainer = null;
    }
    this.container = null;
  }
}

export const overlayRenderer = new OverlayRenderer();
